import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'sonner';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { postNotification } from '../../../models/LocalNotifications';
import { Topics } from '../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../models/Logging';
import {
	ComposeEmailViewModel,
	ContentCalenderSuggestionViewModel,
	IEmailMessageFollowUpOptions,
} from '../../../viewmodels/AppViewModels';
import { SuggestedCampaignOptionsSummary } from '../../components/campaigns/SuggestedCampaignOptionsSummary';
import { EditCampaign } from '../templates/EditCampaign';
import { Search } from '../templates/Search';
import { styleSheet } from './styles';

interface IProps extends Partial<RouteComponentProps<any>>, IModalContext {
	emailComposer?: ComposeEmailViewModel<IEmailMessageFollowUpOptions>;
	onFinish?(): void;
	suggestion?: ContentCalenderSuggestionViewModel;
}

const SuggestedCampaignComposerBase: React.FC<IProps> = observer(props => {
	const { emailComposer, onFinish, suggestion } = props;
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('SuggestedCampaignComposer');
	const h = useHistory();
	const match = useRouteMatch();

	// #region navigation
	const onShowSendOptionsClicked = React.useCallback(() => {
		h.push({
			pathname: `${match.url}/schedule`,
			state: h.location.state,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [match.url, h.location.state]);

	const onEditRecipientsClicked = React.useCallback(() => {
		h.push({
			pathname: `${match.url}/recipients`,
			state: h.location.state,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [match.url, h.location.state]);
	// #endregion

	// #region send
	const approveSuggestion = React.useCallback(async () => {
		try {
			await emailComposer.emailMessage.send(suggestion.id);

			toast.success(`Suggestion Approved`);
			postNotification({
				info: [],
				topic: Topics.EDIT_CAMPAIGNS_ITEM,
			});

			onFinish();
		} catch (error) {
			logApiError('ApproveSuggestion-Error', error);

			errorMessages.pushApiError(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emailComposer, suggestion, onFinish]);

	const approveWithCompliance = React.useCallback(async () => {
		try {
			emailComposer.emailMessage.options.sendWithCompliance = true;

			await emailComposer.emailMessage.send(suggestion.id);

			toast.success(`Suggestion Approved`);
			postNotification({
				info: [],
				topic: Topics.EDIT_CAMPAIGNS_ITEM,
			});

			onFinish();
		} catch (error) {
			logApiError('ApproveWithComplianceSuggestion-Error', error);

			errorMessages.pushApiError(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emailComposer, suggestion, onFinish]);

	const rejectSuggestion = React.useCallback(async () => {
		try {
			await suggestion.reject();

			toast.success(`Suggestion Rejected`);
			postNotification({
				info: [],
				topic: Topics.EDIT_CAMPAIGNS_ITEM,
			});

			onFinish();
		} catch (error) {
			logApiError('RejectSuggestion-Error', error);

			errorMessages.pushApiError(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [suggestion, onFinish]);
	// #endregion

	const onFilterCriteriaUpdated = React.useCallback(
		(explicitelyPreventContactFilterRequest: boolean) => {
			if (explicitelyPreventContactFilterRequest) {
				emailComposer?.setAllowContactFilterTagSearchChangesOverride(false);
			}
			suggestion.userSelectedNewTags = true;
		},
		[emailComposer, suggestion]
	);

	const onContextFinishedEditingClicked = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		h.goBack();
	};

	return (
		<EditCampaign
			emailComposer={emailComposer}
			isQueuedCampaign={true}
			initialEmailBodyTemplate={emailComposer?.suggestion?.template}
			onContextFinishedEditingClicked={onContextFinishedEditingClicked}
			readonlyContent={false}
			styles={[styleSheet.editCampaign]}
		>
			{suggestion?.accountTagsMatchingFilterCriteriaTags?.length || suggestion?.userSelectedNewTags ? (
				h.location.pathname === match.url && Boolean(emailComposer) ? (
					<SuggestedCampaignOptionsSummary
						emailComposer={emailComposer}
						onApproveClicked={approveSuggestion}
						onApproveWithCompliance={approveWithCompliance}
						onChangeScheduleClicked={onShowSendOptionsClicked}
						onEditRecipientsClicked={onEditRecipientsClicked}
						onRejectClicked={rejectSuggestion}
					/>
				) : null
			) : (
				<Search applyOwnedByCriteria={false} nextCtaText='Next: Summary' onNextClick={onFilterCriteriaUpdated} />
			)}
		</EditCampaign>
	);
});

export const SuggestedCampaignComposer = inject(ModalChildComponentContextKey)(SuggestedCampaignComposerBase);
